import React, {useState} from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {toast} from "react-toastify";
import ReactTable from "../../Components/ReactTable";
import {faIcons} from "../../../utils/iconConstants";
import {toastSuccess} from "../../../utils/toast";
import {canonicalStoreOverridesSuccessMessage, uploadCanonicalStoreOverrides} from "../canonicalStoreOverrides";

export const CHANGE_REJECTED = 'rejected';
export const PREVIEW_SUMMARY_COUNTS = [
    {key: 'new', label: 'New'},
    {key: 'changed', label: 'Changed'},
    {key: 'unchanged', label: 'Unchanged'},
    {key: 'rejected', label: 'Rejected'},
    {key: 'skipped', label: 'Skipped'},
];

const TEXT_COLUMN = {filterFn: 'includesString'};

const rejectedCell = ({row, getValue}) => (
    <span className={row.original.change === CHANGE_REJECTED ? 'error' : undefined}>{getValue() ?? ''}</span>
);

const errorReasonsCell = ({row, getValue}) => (getValue() ?? '').split('; ').filter(Boolean).map(reason => (
    <div key={reason} className={row.original.change === CHANGE_REJECTED ? 'error' : undefined}>{reason}</div>
));

const classificationCell = ({getValue}) => getValue()?.toUpperCase() ?? '';

const previewColumns = [
    {header: 'ROW', accessorKey: 'row', size: 70},
    {header: 'CANONICAL STORE', accessorKey: 'canonicalStoreId', size: 300, ...TEXT_COLUMN},
    {header: 'CHANGE', accessorKey: 'change', size: 110, cell: rejectedCell, ...TEXT_COLUMN},
    {header: 'CURRENT OPEN', accessorKey: 'currentOpenDate', size: 168, ...TEXT_COLUMN},
    {header: 'NEW OPEN', accessorKey: 'openDate', size: 130, ...TEXT_COLUMN},
    {header: 'CURRENT CLOSED', accessorKey: 'currentClosedDate', size: 168, ...TEXT_COLUMN},
    {header: 'NEW CLOSED', accessorKey: 'closedDate', size: 130, ...TEXT_COLUMN},
    {header: 'CURRENT CLASSIFICATION', accessorKey: 'currentClassification', size: 250, cell: classificationCell, ...TEXT_COLUMN},
    {header: 'NEW CLASSIFICATION', accessorKey: 'classification', size: 187, cell: classificationCell, ...TEXT_COLUMN},
    {header: 'ERROR', accessorKey: 'error', size: 350, cell: errorReasonsCell, ...TEXT_COLUMN},
];

const CanonicalStoreOverridesPreviewModal = ({version, filename, contents, preview, onClose, onUploaded}) => {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const summary = preview.summary ?? {};
    const hasRejectedRows = (summary.rejected ?? 0) > 0;
    const hasChanges = (summary.new ?? 0) + (summary.changed ?? 0) > 0;
    const canConfirm = !uploading && !hasRejectedRows && hasChanges;

    const onConfirm = () => {
        setUploading(true);
        setUploadError(null);
        return uploadCanonicalStoreOverrides(version, filename, contents)
            .then(response => {
                toastSuccess(toast, canonicalStoreOverridesSuccessMessage(filename, response));
                onUploaded(response);
            })
            .catch(error => {
                setUploadError(error.message);
                setUploading(false);
            });
    };

    return (
        <Modal
            isOpen={true}
            className="Modal store-override-modal store-overrides-preview-modal"
            overlayClassName="Overlay"
            ariaHideApp={false}
            shouldCloseOnOverlayClick={false}
            onRequestClose={() => !uploading && onClose()}
            testId={"store-overrides-preview-modal"}
        >
            <h5 className={"modal-header-text"}>Preview Store Overrides Upload</h5>
            <div className={"store-override-store"}>
                <div className={"store-info-field"}>
                    <div className={"store-info-field-label"}>File</div>
                    <div className={"store-info-field-value"} data-testid={"store-overrides-preview-filename"}>{filename}</div>
                </div>
                <div className={"store-info-field"}>
                    <div className={"store-info-field-label"}>Version</div>
                    <div className={"store-info-field-value"} data-testid={"store-overrides-preview-version"}>{version}</div>
                </div>
            </div>
            <div className={"store-overrides-preview-summary"} data-testid={"store-overrides-preview-summary"}>
                {PREVIEW_SUMMARY_COUNTS.map(({key, label}) => (
                    <span key={key} className={"store-overrides-preview-count"}
                          data-testid={`store-overrides-preview-count-${key}`}>
                        {label}: {summary[key] ?? 0}
                    </span>
                ))}
            </div>
            <ReactTable className={"store-overrides-preview-table"}
                        filterable
                        data={preview.rows ?? []}
                        columns={previewColumns}
                        initialState={{pagination: {pageSize: 10}}}
            />
            {hasRejectedRows && <div className={'error'} role={'alert'}>Fix the rejected rows in the file and upload it again.</div>}
            {!hasRejectedRows && !hasChanges &&
                <div className={'store-overrides-preview-note'} data-testid={'store-overrides-preview-no-changes'}>
                    This file would not change any store overrides.
                </div>}
            {uploadError &&
                <div className={'error'} role={'alert'} data-testid={"store-overrides-preview-upload-error"}>{uploadError}</div>}
            <div className={"right-things"}>
                <button type="button" className="button modal-button cancel" name="cancel"
                        onClick={onClose} disabled={uploading}>
                    Cancel
                </button>
                <button type="button" className={"button modal-button confirm"} name="confirm"
                        onClick={onConfirm} disabled={!canConfirm}>
                    {uploading
                        ? <><i className={`${faIcons.spinner} fa-spin`} data-testid={"spinner"}/><span>Uploading</span></>
                        : "Confirm"}
                </button>
            </div>
        </Modal>
    );
};

CanonicalStoreOverridesPreviewModal.propTypes = {
    version: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
    contents: PropTypes.string.isRequired,
    preview: PropTypes.shape({
        summary: PropTypes.object,
        rows: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    onUploaded: PropTypes.func.isRequired,
};

export default CanonicalStoreOverridesPreviewModal;
